// ── PRINTPLUSH PRINTABILITY CHECK ──
// Measures a quantised mat composite (logo on background, <=12 flat colours) against the PrintPlush
// design guidelines, in REAL MILLIMETRES for the mat size chosen:
//   - stroke / logo thickness  >= 10 mm   (per colour region, measured by morphological opening)
//   - spacing within artwork   >=  5 mm   (the background colour's gaps, same measurement)
//   - text height              >= 50 mm   (rows of small letter-sized components; cap height + stroke width per row)
//   - no border / frame lines             (uniform band around the edge that differs from the inside)
// Pure function, no DOM: works in the browser (on an offscreen canvas's ImageData) and in Node (tests).
// Input pixels are resampled to 1 px = 1 mm so every threshold is a plain pixel count.
(function (root) {
  'use strict';

  // Two tiers per rule: "warn" = what production has actually rejected (shown to the customer);
  // "note" = below the published PrintPlush guideline but usually printable (recorded for production).
  const DEFAULTS = {
    strokeMinMm: 10, strokeWarnMm: 4,   // logo / line thickness
    gapMinMm: 5, gapWarnMm: 3,          // spacing within the artwork (background gaps)
    textMinMm: 50, textWarnMm: 28,      // text height (cap height of the row): 18/18 customer files with caps under 28mm were enlarged by production (2026-09-24, 49 order/proof pairs)
    textStrokeWarnMm: 10,               // text rows with strokes under this are re-set bolder by production (25/25 under 10mm, 2026-09-24)
    fatStrokeMm: 13,                    // short text with strokes this thick prints fine (no height warn)
    thinWarnShare: 0.4, thinWarnMm2: 1500, // colour-level thin-stroke warn: production accepts keylines/rays,
                                        // so only a colour that is MOSTLY thin and substantial is customer-facing
    minClassShare: 0.001, mergeDist: 26, checkBorder: true,
  };

  // Resample RGBA (w x h) to (tw x th) by nearest neighbour.
  function resample(src, w, h, tw, th) {
    const out = new Uint8ClampedArray(tw * th * 4);
    for (let y = 0; y < th; y++) {
      const sy = Math.min(h - 1, Math.floor((y + 0.5) * h / th));
      for (let x = 0; x < tw; x++) {
        const sx = Math.min(w - 1, Math.floor((x + 0.5) * w / tw));
        const s = (sy * w + sx) * 4, o = (y * tw + x) * 4;
        out[o] = src[s]; out[o + 1] = src[s + 1]; out[o + 2] = src[s + 2]; out[o + 3] = src[s + 3];
      }
    }
    return out;
  }

  // Chamfer (3-4) distance transform: distance (in px, approx Euclidean) from every pixel where
  // inside[i]==1 to the nearest pixel where inside[i]==0. Pixels outside the image count as OUTSIDE
  // when outsideIsInside is false, otherwise as inside (used so a colour running off the edge of the
  // padded image is not measured as thinner than it is).
  function chamfer(inside, w, h, outsideIsInside) {
    const INF = 1e9, d = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) d[i] = inside[i] ? INF : 0;
    const edge = outsideIsInside ? INF : 0;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = y * w + x; if (!d[i]) continue;
      let v = d[i];
      const up = y > 0 ? d[i - w] + 3 : edge + 3, left = x > 0 ? d[i - 1] + 3 : edge + 3;
      const ul = (y > 0 && x > 0) ? d[i - w - 1] + 4 : edge + 4, ur = (y > 0 && x < w - 1) ? d[i - w + 1] + 4 : edge + 4;
      if (up < v) v = up; if (left < v) v = left; if (ul < v) v = ul; if (ur < v) v = ur;
      d[i] = v;
    }
    for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) {
      const i = y * w + x; if (!d[i]) continue;
      let v = d[i];
      const dn = y < h - 1 ? d[i + w] + 3 : edge + 3, right = x < w - 1 ? d[i + 1] + 3 : edge + 3;
      const dl = (y < h - 1 && x > 0) ? d[i + w - 1] + 4 : edge + 4, dr = (y < h - 1 && x < w - 1) ? d[i + w + 1] + 4 : edge + 4;
      if (dn < v) v = dn; if (right < v) v = right; if (dl < v) v = dl; if (dr < v) v = dr;
      d[i] = v;
    }
    return d; // units: 3 = one pixel
  }

  // Pixels of `mask` that do NOT survive a morphological opening by a disc of radius r px, i.e. the
  // parts of the region thinner than 2r. Returns {thin: Uint8Array, count}.
  function thinParts(mask, w, h, r) {
    const R = r * 3; // chamfer units
    const dIn = chamfer(mask, w, h, true);
    const eroded = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) eroded[i] = dIn[i] > R ? 1 : 0;
    // dilate(eroded, r) = pixels within r of an eroded pixel = complement-of-eroded distance <= r
    const notEroded = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) notEroded[i] = eroded[i] ? 0 : 1;
    const dOut = chamfer(notEroded, w, h, false);
    const thin = new Uint8Array(w * h); let count = 0;
    for (let i = 0; i < w * h; i++) if (mask[i] && dOut[i] > R) { thin[i] = 1; count++; }
    return { thin, count };
  }

  // Connected components (4-connected) of a mask; returns array of {x0,y0,x1,y1,n}
  function components(mask, w, h, minPx) {
    const seen = new Uint8Array(w * h), out = [], stack = new Int32Array(w * h);
    for (let s = 0; s < w * h; s++) {
      if (!mask[s] || seen[s]) continue;
      let sp = 0; stack[sp++] = s; seen[s] = 1;
      let x0 = w, y0 = h, x1 = 0, y1 = 0, n = 0;
      while (sp) {
        const i = stack[--sp]; const x = i % w, y = (i - x) / w; n++;
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
        if (x > 0 && mask[i - 1] && !seen[i - 1]) { seen[i - 1] = 1; stack[sp++] = i - 1; }
        if (x < w - 1 && mask[i + 1] && !seen[i + 1]) { seen[i + 1] = 1; stack[sp++] = i + 1; }
        if (y > 0 && mask[i - w] && !seen[i - w]) { seen[i - w] = 1; stack[sp++] = i - w; }
        if (y < h - 1 && mask[i + w] && !seen[i + w]) { seen[i + w] = 1; stack[sp++] = i + w; }
      }
      if (n >= minPx) out.push({ x0, y0, x1, y1, n, w: x1 - x0 + 1, h: y1 - y0 + 1 });
    }
    return out;
  }

  // Strings of letter-sized components = small text (any angle: letters chain to a neighbour of similar size
  // within ~2.5 letter-heights, so rotated or curved words are found too). Returns [{heightMm,count,bbox}].
  function smallTextRows(comps, textMinMm) {
    const letters = comps.filter(c => c.h >= 4 && c.h < textMinMm && c.w <= c.h * 3 && c.w >= 2 && c.n >= 6);
    const n = letters.length, parent = new Int32Array(n); for (let i = 0; i < n; i++) parent[i] = i;
    const find = i => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
    for (let i = 0; i < n; i++) {
      const a = letters[i], acx = (a.x0 + a.x1) / 2, acy = (a.y0 + a.y1) / 2;
      for (let j = i + 1; j < n; j++) {
        const b = letters[j];
        if (Math.max(a.h, b.h) > 2.2 * Math.min(a.h, b.h)) continue;
        const bcx = (b.x0 + b.x1) / 2, bcy = (b.y0 + b.y1) / 2, reach = 2.5 * Math.max(a.h, b.h);
        if (Math.abs(acx - bcx) > reach || Math.abs(acy - bcy) > reach) continue;
        // gap between bboxes (letters in a word nearly touch; a scattered pattern does not)
        const gx = Math.max(0, Math.max(a.x0, b.x0) - Math.min(a.x1, b.x1)), gy = Math.max(0, Math.max(a.y0, b.y0) - Math.min(a.y1, b.y1));
        if (Math.max(gx, gy) > 1.5 * Math.max(a.h, b.h)) continue;
        const ra = find(i), rb = find(j); if (ra !== rb) parent[ra] = rb;
      }
    }
    const groups = new Map();
    for (let i = 0; i < n; i++) { const r = find(i); if (!groups.has(r)) groups.set(r, []); groups.get(r).push(letters[i]); }
    const rows = [];
    for (const g of groups.values()) {
      if (g.length < 4) continue; // 3 blobs together (paw pads, leaves) are not a word
      const hs0 = g.map(c => c.h).sort((p, q) => p - q);
      if (g.length < 6 && hs0[Math.floor(0.8 * (g.length - 1))] < 12) continue; // 4-5 specks under 12mm (rays, petals, dots) are not text
      const hs = g.map(c => c.h).sort((p, q) => p - q), medH = hs[hs.length >> 1];
      // capMm ~ cap/ascender height (80th percentile of letter heights): for mixed-case text the median is the
      // x-height, which under-reads the size the guideline talks about; for all-caps text both are the same.
      const capH = hs[Math.min(hs.length - 1, Math.floor(0.8 * (hs.length - 1)))];
      rows.push({ heightMm: medH, capMm: capH, count: g.length, bbox: { x0: Math.min(...g.map(c => c.x0)), y0: Math.min(...g.map(c => c.y0)), x1: Math.max(...g.map(c => c.x1)), y1: Math.max(...g.map(c => c.y1)) } });
    }
    return rows;
  }

  // Border/frame detection on a class map: a thin uniform band along >=2 sides that differs from the band inside it.
  function detectBorder(cls, w, h) {
    const share = (x0, y0, x1, y1) => {
      const cnt = {}; let n = 0;
      for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) { const c = cls[y * w + x]; cnt[c] = (cnt[c] || 0) + 1; n++; }
      let best = -1, bc = 0; for (const k in cnt) if (cnt[k] > bc) { bc = cnt[k]; best = +k; }
      return { c: best, s: n ? bc / n : 0 };
    };
    const A0 = 2, A1 = 10, B0 = 16, B1 = 26; // mm from the edge
    if (w < 2 * B1 + 10 || h < 2 * B1 + 10) return { sides: 0 };
    const tests = [
      [share(0, A0, w, A1), share(0, B0, w, B1)],             // top
      [share(0, h - A1, w, h - A0), share(0, h - B1, w, h - B0)], // bottom
      [share(A0, 0, A1, h), share(B0, 0, B1, h)],             // left
      [share(w - A1, 0, w - A0, h), share(w - B1, 0, w - B0, h)], // right
    ];
    let sides = 0;
    for (const [a, b] of tests) if (a.s >= 0.85 && b.s >= 0.6 && a.c !== b.c) sides++;
    return { sides };
  }

  function hex(r, g, b) { return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase(); }

  // MAIN. rgba: Uint8ClampedArray of a composite (background colour + logo, already quantised);
  // w,h its size; mmPerPx: millimetres per source pixel. opts: see DEFAULTS. Returns a report.
  function printCheck(rgba, w, h, mmPerPx, opts) {
    const o = Object.assign({}, DEFAULTS, opts || {});
    const t0 = Date.now();
    // 1. resample to 1 px = 1 mm
    const tw = Math.max(1, Math.round(w * mmPerPx)), th = Math.max(1, Math.round(h * mmPerPx));
    const px = (tw === w && th === h) ? rgba : resample(rgba, w, h, tw, th);
    const N = tw * th;
    // 2. colour classes: exact colours with a meaningful share; everything else snaps to the nearest
    const counts = new Map(); let transparent = 0;
    for (let i = 0; i < N; i++) {
      const a = px[i * 4 + 3]; if (a < 10) { transparent++; continue; }
      const key = (px[i * 4] << 16) | (px[i * 4 + 1] << 8) | px[i * 4 + 2];
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const raw = [...counts.entries()].filter(([, n]) => n >= N * o.minClassShare).sort((a, b) => b[1] - a[1])
      .map(([key, n]) => ({ r: key >> 16, g: (key >> 8) & 255, b: key & 255, hex: hex(key >> 16, (key >> 8) & 255, key & 255), count: n }));
    // merge near-identical colours (mat-grain noise on the background, JPEG ringing) into the bigger one;
    // distinct chart colours are far further apart than this
    const MERGE2 = (o.mergeDist || 20) ** 2, classes = [];
    for (const c of raw) {
      if (classes.some(q => (q.r - c.r) ** 2 + (q.g - c.g) ** 2 + (q.b - c.b) ** 2 <= MERGE2)) continue;
      classes.push(c);
    }
    const TRANSPARENT = 250; // fixed class id for alpha (cls is a Uint8Array; real classes are <= 12 + detail classes)
    const cls = new Uint8Array(N);
    const nearestCache = new Map();
    for (let i = 0; i < N; i++) {
      const a = px[i * 4 + 3];
      if (a < 10) { cls[i] = TRANSPARENT; continue; }
      const key = (px[i * 4] << 16) | (px[i * 4 + 1] << 8) | px[i * 4 + 2];
      let c = nearestCache.get(key);
      if (c === undefined) {
        let best = 0, bd = Infinity; const r = key >> 16, g = (key >> 8) & 255, b = key & 255;
        for (let k = 0; k < classes.length; k++) { const q = classes[k]; const d = (q.r - r) ** 2 + (q.g - g) ** 2 + (q.b - b) ** 2; if (d < bd) { bd = d; best = k; } }
        c = best; nearestCache.set(key, c);
      }
      cls[i] = c;
    }
    for (const c of classes) c.count = 0;
    for (let i = 0; i < N; i++) if (cls[i] !== TRANSPARENT) classes[cls[i]].count++;
    // 3. background class: transparency if present, else the dominant colour when it clearly dominates
    let bgClass = -1, biggest = -1;
    for (let k = 0; k < classes.length; k++) if (biggest < 0 || classes[k].count > classes[biggest].count) biggest = k;
    if (transparent > N * 0.05) bgClass = TRANSPARENT;
    else if (biggest >= 0 && classes[biggest].count >= N * 0.3) bgClass = biggest;
    // 3b. DETAILS DRAWN IN THE BACKGROUND COLOUR (e.g. dark text on a white panel inside a dark design): small
    // islands of the background colour are foreground elements, not background. Split them into their own
    // class so they are measured with the stroke/text rules instead of the spacing rule.
    let bgDetailClass = -1;
    if (bgClass >= 0 && bgClass !== TRANSPARENT) {
      const bgMask = new Uint8Array(N); for (let i = 0; i < N; i++) bgMask[i] = cls[i] === bgClass ? 1 : 0;
      const comps = components(bgMask, tw, th, 1);
      const maxIsland = Math.max(50, N * 0.02);
      const islands = comps.filter(c => c.n <= maxIsland && c.w < tw * 0.5 && c.h < th * 0.5);
      if (islands.length) {
        bgDetailClass = classes.length;
        classes.push({ r: classes[bgClass].r, g: classes[bgClass].g, b: classes[bgClass].b, hex: classes[bgClass].hex, count: 0, detail: true });
        // relabel island pixels (flood again per island bbox for speed)
        const seen = new Uint8Array(N), stack = new Int32Array(N);
        for (const isl of islands) {
          const s0 = isl.y0 * tw + isl.x0; // find a seed inside the bbox belonging to this island
          let seed = -1;
          for (let y = isl.y0; y <= isl.y1 && seed < 0; y++) for (let x = isl.x0; x <= isl.x1; x++) { const i = y * tw + x; if (bgMask[i] && !seen[i]) { seed = i; break; } }
          if (seed < 0) continue;
          let sp = 0; stack[sp++] = seed; seen[seed] = 1;
          while (sp) {
            const i = stack[--sp]; const x = i % tw, y = (i - x) / tw;
            cls[i] = bgDetailClass; classes[bgDetailClass].count++; classes[bgClass].count--;
            if (x > 0 && bgMask[i - 1] && !seen[i - 1]) { seen[i - 1] = 1; stack[sp++] = i - 1; }
            if (x < tw - 1 && bgMask[i + 1] && !seen[i + 1]) { seen[i + 1] = 1; stack[sp++] = i + 1; }
            if (y > 0 && bgMask[i - tw] && !seen[i - tw]) { seen[i - tw] = 1; stack[sp++] = i - tw; }
            if (y < th - 1 && bgMask[i + tw] && !seen[i + tw]) { seen[i + tw] = 1; stack[sp++] = i + tw; }
          }
        }
      }
    }
    // 4. pad by r+2 px of background so edges are measured correctly
    const rMax = Math.ceil(o.strokeMinMm / 2) + 2, pw = tw + 2 * rMax, ph = th + 2 * rMax;
    const padClass = bgClass >= 0 ? bgClass : TRANSPARENT;
    const pcls = new Uint8Array(pw * ph).fill(padClass);
    for (let y = 0; y < th; y++) pcls.set(cls.subarray(y * tw, (y + 1) * tw), (y + rMax) * pw + rMax);
    const overlay = new Uint8Array(N);
    const report = [];
    const allIds = classes.map((_, k) => k); if (transparent > 0) allIds.push(TRANSPARENT);
    for (const k of allIds) {
      const isBg = k === bgClass;
      const mask = new Uint8Array(pw * ph); let mcount = 0;
      for (let i = 0; i < pw * ph; i++) if (pcls[i] === k) { mask[i] = 1; mcount++; }
      if (!mcount) continue;
      const guideMm = isBg ? o.gapMinMm : o.strokeMinMm, warnMm = isBg ? o.gapWarnMm : o.strokeWarnMm;
      const warn = thinParts(mask, pw, ph, warnMm / 2);
      const note = thinParts(mask, pw, ph, guideMm / 2);
      // overlay = the parts production would reject (warn tier); copy back into the unpadded frame
      for (let y = 0; y < th; y++) for (let x = 0; x < tw; x++) if (warn.thin[(y + rMax) * pw + x + rMax]) overlay[y * tw + x] = 1;
      const info = { id: k, hex: k === TRANSPARENT ? null : classes[k].hex, isBg, areaMm2: mcount,
        warnMm, warnMm2: warn.count, warnShare: warn.count / mcount,
        guideMm, noteMm2: note.count, noteShare: note.count / mcount };
      // small text rows (stroke colours only), each with its typical stroke width:
      // for a stroke of width w the mean distance-to-edge over its pixels is w/4, so w = 4 x mean(dist)
      if (!isBg) {
        const comps = components(mask, pw, ph, 6);
        const dIn = chamfer(mask, pw, ph, true);
        info.textRows = smallTextRows(comps, o.textMinMm).map(r => {
          let sum = 0, n = 0;
          for (let y = r.bbox.y0; y <= r.bbox.y1; y++) for (let x = r.bbox.x0; x <= r.bbox.x1; x++) { const i = y * pw + x; if (mask[i]) { sum += dIn[i] / 3; n++; } }
          return { heightMm: r.heightMm, capMm: r.capMm, count: r.count, strokeMm: n ? Math.round(4 * sum / n * 10) / 10 : 0, bbox: { x0: r.bbox.x0 - rMax, y0: r.bbox.y0 - rMax, x1: r.bbox.x1 - rMax, y1: r.bbox.y1 - rMax } };
        });
      }
      report.push(info);
    }
    const border = o.checkBorder ? detectBorder(cls, tw, th) : { sides: 0 };
    // 5. issues (each carries level 'warn' or 'note')
    const issues = [];
    for (const c of report) {
      if (!c.isBg) {
        if (c.warnMm2 >= o.thinWarnMm2 && c.warnShare >= o.thinWarnShare) issues.push({ level: 'warn', type: 'thin-stroke', hex: c.hex, mm2: c.warnMm2, share: c.warnShare, minMm: c.warnMm });
        else if ((c.warnMm2 >= 150 && c.warnShare >= 0.15) || (c.noteMm2 >= 300 && c.noteShare >= 0.15)) issues.push({ level: 'note', type: 'thin-stroke', hex: c.hex, mm2: c.noteMm2, share: c.noteShare, minMm: c.guideMm });
      } else {
        // spacing: production has never rejected on spacing alone, so this is recorded for them, not shown to the customer
        if (c.warnMm2 >= 150) issues.push({ level: 'note', type: 'tight-spacing', hex: c.hex, mm2: c.warnMm2, minMm: c.warnMm });
        else if (c.noteMm2 >= 500) issues.push({ level: 'note', type: 'tight-spacing', hex: c.hex, mm2: c.noteMm2, minMm: c.guideMm });
      }
      for (const r of (c.textRows || [])) {
        const fat = r.strokeMm >= o.fatStrokeMm && r.capMm >= 15;
        const small = r.capMm < o.textWarnMm && !fat, light = r.strokeMm < o.textStrokeWarnMm;
        issues.push({ level: (small || light) ? 'warn' : 'note', type: 'small-text', reason: small ? (light ? 'small+light' : 'small') : light ? 'light' : 'guideline', hex: c.hex, heightMm: r.heightMm, capMm: r.capMm, strokeMm: r.strokeMm, count: r.count, bbox: r.bbox, minMm: small ? o.textWarnMm : o.textMinMm, minStrokeMm: o.textStrokeWarnMm });
      }
    }
    if (border.sides >= 2) issues.push({ level: 'warn', type: 'border', sides: border.sides });
    const warnCount = issues.filter(i => i.level === 'warn').length;
    return { ok: warnCount === 0, warnCount, noteCount: issues.length - warnCount, issues, classes: report, overlay, width: tw, height: th, mmPerPx: 1, ms: Date.now() - t0, bgClass };
  }

  const api = { printCheck, chamfer, thinParts, components, smallTextRows, detectBorder, resample };
  if (typeof module !== 'undefined' && module.exports) module.exports = api; else root.TMGPrintCheck = api;
})(typeof window !== 'undefined' ? window : globalThis);
